import React, { useEffect, useState } from 'react';
import { Alert, Container, Spinner } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import EventDetails from './Events/EventDetails';
import PageWrapper from './common/PageWrapper';
import './EventPage.css';
import { useDocumentMetadata } from '../hooks/useDocumentMetadata';
import { WordPressEvent, wordpressApi } from '../services/wordpress';
import { formatEventDate } from '../utils/dateUtils';
import { useLocalization } from '../localization';
import {
  buildCanonicalUrl,
  buildEventStructuredData,
  buildGoogleCalendarUrl,
  getEventDescription,
  getEventImageUrl,
  getEventTitle,
  parseEventDateTime,
} from '../utils/eventUtils';

const getMonthFromEvent = (event: WordPressEvent): number => {
  const month = Number(event.acf?.event_date?.split('/')[1]);
  return Number.isNaN(month) || month < 1 || month > 12 ? 1 : month;
};

const getHueFromRGB = (color: string): number => {
  const normalizedColor = color.trim().replace('#', '');
  const r = parseInt(normalizedColor.slice(0, 2), 16) / 255;
  const g = parseInt(normalizedColor.slice(2, 4), 16) / 255;
  const b = parseInt(normalizedColor.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let hue = 0;

  if (max !== min) {
    const delta = max - min;
    switch (max) {
      case r:
        hue = (g - b) / delta + (g < b ? 6 : 0);
        break;
      case g:
        hue = (b - r) / delta + 2;
        break;
      default:
        hue = (r - g) / delta + 4;
        break;
    }
    hue *= 60;
  }

  return hue;
};

const EventPage: React.FC = () => {
  const { copy, locale } = useLocalization();
  const { slug = '' } = useParams();
  const [event, setEvent] = useState<WordPressEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shareLabel, setShareLabel] = useState(copy.events.share);
  const [showCopiedTooltip, setShowCopiedTooltip] = useState(false);

  useEffect(() => {
    setShareLabel(copy.events.share);
  }, [copy.events.share]);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await wordpressApi.getEventBySlug(slug);
        setEvent(result);
      } catch (fetchError) {
        console.error('Error loading event page:', fetchError);
        setError(copy.events.eventLoadError);
      } finally {
        setLoading(false);
      }
    };

    void fetchEvent();
  }, [copy.events.eventLoadError, slug]);

  const title = event ? `${getEventTitle(event)} | El Pumarejo` : copy.events.eventFallbackTitle;
  const description = event
    ? getEventDescription(event).slice(0, 160)
    : copy.events.eventFallbackDescription;
  const canonicalUrl = buildCanonicalUrl(window.location.pathname);
  const structuredData = event ? buildEventStructuredData(event, canonicalUrl) : undefined;

  useDocumentMetadata({
    title,
    description,
    canonicalUrl,
    jsonLd: structuredData,
  });

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">{copy.events.loading}</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  if (!event) {
    return (
      <Container className="py-5">
        <Alert variant="warning">{copy.events.eventNotFound}</Alert>
      </Container>
    );
  }

  const eventDate = parseEventDateTime(event.acf?.event_date, event.acf?.event_time);
  const formattedDate = formatEventDate(
    event.acf?.event_date,
    event.acf?.event_time,
    event.acf?.closing_time,
    'default',
    locale
  );
  const eventTitle = getEventTitle(event);
  const eventDescription = getEventDescription(event);
  const month = getMonthFromEvent(event);
  const calendarUrl = buildGoogleCalendarUrl(event, canonicalUrl);
  const primaryColor = getComputedStyle(document.documentElement).getPropertyValue(
    `--${month}-principal`
  );
  const primColorHue = getHueFromRGB(primaryColor);
  const socialIconStyle = {
    filter: `brightness(0) invert(0.3) sepia(1) saturate(1) hue-rotate(${primColorHue}deg)`,
  };

  const handleShare = async () => {
    const isMobileLike =
      window.matchMedia('(max-width: 768px)').matches || navigator.maxTouchPoints > 0;

    try {
      if (navigator.share && isMobileLike) {
        await navigator.share({
          title: eventTitle,
          text: eventDescription,
          url: canonicalUrl,
        });
        return;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(canonicalUrl);
      } else {
        const input = document.createElement('input');
        input.value = canonicalUrl;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
      }

      setShareLabel(copy.events.linkCopied);
      setShowCopiedTooltip(true);
      window.setTimeout(() => setShareLabel(copy.events.share), 2000);
      window.setTimeout(() => setShowCopiedTooltip(false), 2000);
    } catch (shareError) {
      if (shareError instanceof DOMException && shareError.name === 'AbortError') {
        return;
      }

      console.error('Unable to share event:', shareError);
    }
  };

  return (
    <Container>
      <PageWrapper title={eventTitle} containerClassName="page-container-complete-border">
        <article className="event-page">
          <Link to="/" className="event-page-back-link" aria-label={copy.events.backToEvents}>
            <svg aria-hidden="true" viewBox="0 0 24 24" className="event-page-back-icon">
              <path d="M14.71 5.29a1 1 0 0 1 0 1.42L10.41 11H20a1 1 0 1 1 0 2h-9.59l4.3 4.29a1 1 0 0 1-1.42 1.42l-6-6a1 1 0 0 1 0-1.42l6-6a1 1 0 0 1 1.42 0Z" />
            </svg>
          </Link>
          <div className="event-page-hero">
            <img src={getEventImageUrl(event)} alt={eventTitle} className="event-page-image" />
            <div className="event-page-summary">
              <p className="event-page-date">{formattedDate}</p>
              <p className="event-page-location">
                El Pumarejo
                <br />
                L'Hospitalet de Llobregat
              </p>
              {eventDate ? (
                <p className="event-page-machine-date">
                  {new Intl.DateTimeFormat(locale, {
                    dateStyle: 'full',
                    timeStyle: 'short',
                  }).format(eventDate)}
                </p>
              ) : null}
              {event.acf?.link_tickets || calendarUrl ? (
                <div className="event-page-actions">
                  {event.acf?.link_tickets ? (
                    <a
                      href={event.acf.link_tickets}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="event-page-ticket-link"
                    >
                      {copy.events.tickets}
                    </a>
                  ) : null}
                  {calendarUrl ? (
                    <a
                      href={calendarUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="event-page-calendar-link"
                      aria-label={copy.events.addToCalendar}
                      title={copy.events.addToCalendar}
                    >
                      <svg
                        aria-hidden="true"
                        viewBox="0 0 24 24"
                        className="event-page-calendar-icon"
                      >
                        <path d="M7 2a1 1 0 0 1 1 1v1h8V3a1 1 0 1 1 2 0v1h1a3 3 0 0 1 3 3v11a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V7a3 3 0 0 1 3-3h1V3a1 1 0 0 1 1-1Zm13 8H4v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8ZM5 6a1 1 0 0 0-1 1v1h16V7a1 1 0 0 0-1-1H5Zm7 5a1 1 0 0 1 1 1v1h1a1 1 0 1 1 0 2h-1v1a1 1 0 1 1-2 0v-1h-1a1 1 0 1 1 0-2h1v-1a1 1 0 0 1 1-1Z" />
                      </svg>
                    </a>
                  ) : null}
                  <div className="event-page-share-wrap">
                    <button
                      type="button"
                      className="event-page-share-button"
                      aria-label={shareLabel}
                      title={shareLabel}
                      onClick={() => {
                        void handleShare();
                      }}
                    >
                      <svg aria-hidden="true" viewBox="0 0 24 24" className="event-page-share-icon">
                      <path xmlns="http://www.w3.org/2000/svg" d="M17,22c-.8,0-1.5-.3-2.1-.9-.6-.6-.9-1.3-.9-2.1s0-.3,0-.7l-7-4.1c-.3.3-.6.4-.9.6s-.7.2-1.1.2c-.8,0-1.5-.3-2.1-.9-.6-.6-.9-1.3-.9-2.1s.3-1.5.9-2.1c.6-.6,1.3-.9,2.1-.9s.8,0,1.1.2.7.3.9.6l7-4.1c0-.1,0-.2,0-.3s0-.2,0-.4c0-.8.3-1.5.9-2.1s1.3-.9,2.1-.9,1.5.3,2.1.9.9,1.3.9,2.1-.3,1.5-.9,2.1-1.3.9-2.1.9-.8,0-1.1-.2-.7-.3-.9-.6l-7,4.1c0,.1,0,.2,0,.3s0,.2,0,.4,0,.3,0,.4,0,.2,0,.3l7,4.1c.3-.3.6-.4.9-.6s.7-.2,1.1-.2c.8,0,1.5.3,2.1.9s.9,1.3.9,2.1-.3,1.5-.9,2.1c-.6.6-1.3.9-2.1.9ZM17,20c.3,0,.5,0,.7-.3s.3-.4.3-.7,0-.5-.3-.7-.4-.3-.7-.3-.5,0-.7.3-.3.4-.3.7,0,.5.3.7.4.3.7.3ZM5,13c.3,0,.5,0,.7-.3s.3-.4.3-.7,0-.5-.3-.7c-.2-.2-.4-.3-.7-.3s-.5,0-.7.3c-.2.2-.3.4-.3.7s0,.5.3.7.4.3.7.3ZM17.7,5.7c.2-.2.3-.4.3-.7s0-.5-.3-.7-.4-.3-.7-.3-.5,0-.7.3-.3.4-.3.7,0,.5.3.7.4.3.7.3.5,0,.7-.3Z"/>
                      </svg>
                    </button>
                    {showCopiedTooltip ? (
                      <span className="event-page-share-tooltip" role="status" aria-live="polite">
                        {copy.events.linkCopied}
                      </span>
                    ) : null}
                  </div>
                </div>
              ) : null}
              {event.acf?.link_bandcamp ||
              event.acf?.link_instagram ||
              (event.acf?.link_youtube && event.acf?.link_streaming) ? (
                <div className="event-page-social-links">
                  {event.acf?.link_bandcamp ? (
                    <a
                      href={event.acf.link_bandcamp}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Bandcamp"
                      title="Bandcamp"
                    >
                      <img
                        src="/img/bc-logo.png"
                        alt="Bandcamp"
                        className="event-page-social-logo"
                        style={socialIconStyle}
                      />
                    </a>
                  ) : null}
                  {event.acf?.link_instagram ? (
                    <a
                      href={event.acf.link_instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Instagram"
                      title="Instagram"
                    >
                      <img
                        src="/img/ig-logo.png"
                        alt="Instagram"
                        className="event-page-social-logo"
                        style={socialIconStyle}
                      />
                    </a>
                  ) : null}
                  {event.acf?.link_youtube && event.acf?.link_streaming ? (
                    <a
                      href={event.acf.link_streaming}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Spotify"
                      title="Spotify"
                    >
                      <img
                        src="/img/sp-logo.png"
                        alt="Spotify"
                        className="event-page-social-logo"
                        style={socialIconStyle}
                      />
                    </a>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
          <div className="event-page-body">
            <p className="event-page-description">{eventDescription}</p>
            {event.acf?.genre ? (
              <p className="event-page-meta">
                <strong>{copy.events.genre}</strong> {event.acf.genre}
              </p>
            ) : null}
            {event.acf?.price_description ? (
              <p className="event-page-meta">
                <strong>{copy.events.price}</strong> {event.acf.price_description}
              </p>
            ) : null}
            <EventDetails
              event={event}
              isExpanded={true}
              primColorHue={primColorHue}
              showArtistLinks={false}
            />
          </div>
        </article>
      </PageWrapper>
    </Container>
  );
};

export default EventPage;
